import React, { useContext, useState } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
} from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../contexts/GlobalContext"
import { useQuery } from "@tanstack/react-query"
import { api } from "../../api"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomDatePicker from "../../components/CustomDatePicker"
import dayjs from "dayjs"

import getIconComponent from "../utils/getIconComponent"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

// Lista estática de estados estándar para una Solicitud de Ingreso en VB6
const listaEstados = [
  { id: "PENDIENTE", label: "PENDIENTE" },
  { id: "APROBADA", label: "APROBADA" },
  { id: "ENT.PARCIAL", label: "ENTREGA PARCIAL" },
  { id: "CERRADA", label: "CERRADA" },
  { id: "NEGADA", label: "NEGADA" },
]

const CrearSolicitudIngCliIndex = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Controladores de estado visual y ejecución de la tabla
  const [expanded, setExpanded] = useState(false)
  const [refreshTableKey, setRefreshTableKey] = useState(0)

  // Estado centralizado de filtros principales
  const [filtros, setFiltros] = useState({
    sgasoling: "",
    sgadescri: "",
    motcodigo: "",
    sgastatus: "",
    fecDes: null,
    fecHas: null,
  })

  // Consulta inicial para extraer los catálogos de filtro (Usamos el mismo endpoint base)
  const { data: rawCatalogos, isLoading: isLoadingCatalogos } = useQuery({
    queryKey: ["getCatalogosFiltrosSolIngCli"],
    queryFn: async () => {
      try {
        const response = await api.post("/CrearSolicitudIngCli/getCatalogosIngreso", {})

        let catalogos = {}
        // Extracción profunda en múltiples niveles
        const res = response?.data?.data || response?.data || response || {}

        // Verificamos en qué capa exacta viene la data real
        if (res.cliente_asignado) {
          catalogos = res
        } else if (res.data && res.data.cliente_asignado) {
          catalogos = res.data
        }

        return catalogos
      } catch (error) {
        console.error("Error al cargar catálogos de filtro:", error)
        return {}
      }
    },
    refetchOnWindowFocus: false,
  })

  // Extracción Defensiva OBLIGATORIA (Regla Paso 2)
  const baseCatalogos = rawCatalogos || {}
  const rawMotivos = baseCatalogos?.motivos || []
  const listaMotivos = Array.isArray(rawMotivos) ? rawMotivos : []

  // Ahora extraerá el cliente correctamente porque el queryFn ya desempacó el JSON
  const clienteAsignado = baseCatalogos?.cliente_asignado || null

  const handleInputChange = (field, value) => {
    setFiltros((prev) => ({ ...prev, [field]: value }))
  }

  const handleFiltrar = () => {
    // Al incrementar el key, la tabla montada recibe el endpoint y dispara la petición Axios
    setRefreshTableKey((prev) => prev + 1)
    setExpanded(false)
  }

  // Renderizador de Colores para la Columna de Estado
  const renderEstado = (status) => {
    const statusMap = {
      PENDIENTE: { color: "#ed6c02" }, // Naranja
      APROBADA: { color: "#1976d2" }, // Azul
      "ENT.PARCIAL": { color: "#9c27b0" }, // Morado
      CERRADA: { color: "#2e7d32" }, // Verde
      NEGADA: { color: "#d32f2f" }, // Rojo
    }
    const current = statusMap[status] || { color: "black" }
    return <span style={{ color: current.color, fontWeight: "bold" }}>{status}</span>
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Gestión de Solicitudes de Ingreso (Clientes)</b>
        </div>

        <CustomBackdrop isLoading={isLoadingCatalogos} />

        <Box className={StyledRoot}>
          {/* ACORDEÓN DE FILTROS */}
          <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{ mb: 3, borderRadius: 2, boxShadow: 3, "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f5f7fa", borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Parámetros de Búsqueda
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} alignItems="center">
                {/* Información Visual del Cliente */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Filtrando para Cliente"
                    size="small"
                    value={clienteAsignado ? clienteAsignado.label : "Cargando..."}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ bgcolor: "#e8f5e9" }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Número de Solicitud"
                    size="small"
                    value={filtros.sgasoling}
                    onChange={(e) => handleInputChange("sgasoling", e.target.value.toUpperCase())}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Descripción (Detalle)"
                    size="small"
                    value={filtros.sgadescri}
                    onChange={(e) => handleInputChange("sgadescri", e.target.value.toUpperCase())}
                  />
                </Grid>

                {/* Combos y Fechas */}
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={listaMotivos}
                    getOptionLabel={(option) => option?.label || ""}
                    value={listaMotivos.find((o) => String(o.id) === String(filtros.motcodigo)) || null}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(event, newValue) => {
                      handleInputChange("motcodigo", newValue ? String(newValue.id) : "")
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Motivo de Ingreso"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Estado"
                    size="small"
                    value={filtros.sgastatus}
                    onChange={(e) => handleInputChange("sgastatus", e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todos</em>
                    </MenuItem>
                    {listaEstados.map((op) => (
                      <MenuItem key={op.id} value={op.id}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    label="Fecha Emisión Desde"
                    value={filtros.fecDes}
                    setValue={(v) => handleInputChange("fecDes", v)}
                    isOptional={true}
                    format="DD/MM/YYYY"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    label="Fecha Emisión Hasta"
                    value={filtros.fecHas}
                    setValue={(v) => handleInputChange("fecHas", v)}
                    isOptional={true}
                    format="DD/MM/YYYY"
                  />
                </Grid>

                <Grid item xs={12} display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SearchIcon />}
                    onClick={handleFiltrar}
                    sx={{ px: 4, fontWeight: "bold" }}
                  >
                    Ejecutar Búsqueda
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* TABLA DE RESULTADOS - LLAMA AL BACKEND (Endpoint a crear) */}
          <CustomConditionalActionsTableServer
            key={refreshTableKey}
            // Si la llave es 0 (carga inicial), no dispara el request.
            endpoint={refreshTableKey === 0 ? "" : "/CrearSolicitudIngCli/getAllSolicitudesIngreso"}
            endpointJson={{
              externalFilters: {
                ...filtros,
                fecDes: filtros.fecDes ? dayjs(filtros.fecDes).format("YYYY-MM-DD") : "",
                fecHas: filtros.fecHas ? dayjs(filtros.fecHas).format("YYYY-MM-DD") : "",
              },
            }}
            errorMsgFilterSearch="Error al cargar las solicitudes de ingreso."
            queryKeyModal="SolIngCliData"
            perPage={15}
            rowActionsWidthTable={100}
            rowActions={(row) => {
              const acciones = []

              const editarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "EDITAR",
              )

              const buscarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "BUSCAR",
              )
              // REGLA DE NEGOCIO (VB6): Solo permite modificar si la solicitud está PENDIENTE
              if (editarAction && row.original.sgastatus === "PENDIENTE") {
                acciones.push({
                  label: "Editar Solicitud",
                  key: "EDITAR",
                  icon: getIconComponent(editarAction?.accnameicono, editarAction?.acctipoico),
                  onClick: (row) =>
                    navigate("editar", {
                      state: { sgasoling: row.original.sgasoling },
                    }),
                })
              }

              acciones.push({
                label: "Buscar Solicitud",
                key: "BUSCAR",
                icon: getIconComponent(buscarAction?.accnameicono, buscarAction?.acctipoico),
                onClick: (row) =>
                  navigate("buscar", {
                    state: { sgasoling: row.original.sgasoling },
                  }),
              })

              return acciones
            }}
            topToolbarCustomActions={({ table, device }) => {
              const crearAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action.acccaption === "CREAR")

              const toolbarActions = []
              if (crearAction) {
                toolbarActions.push({
                  label: crearAction?.acccaption,
                  key: crearAction?.acccaption,
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => navigate("crear"), // Nos lleva a la pantalla tipo Amazon
                })
              }
              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "sgasoling",
                header: "Nº Solicitud",
                size: 150,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "sgafecsol",
                header: "Emisión",
                size: 110,
                Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
              },
              {
                accessorKey: "motdescripcion",
                header: "Motivo",
                size: 200,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "sgafecllegada",
                header: "Fecha Llegada",
                size: 130,
                Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
              },
              {
                accessorKey: "sgadescri",
                header: "Descripción",
                size: 250,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "sgastatus",
                header: "Estado",
                size: 130,
                Cell: ({ cell }) => renderEstado(cell.getValue()),
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearSolicitudIngCliIndex
